/**
 * Carrega membros elegíveis para transferência de tarefa.
 * SSOT: user_organizations + user_profiles (sem embed auth.users).
 */
import { supabase } from '../../../lib/supabaseClient';

export interface TransferMember {
  id: string;
  name: string;
  email: string;
  departmentId: string | null;
  roleId: string | null;
  roleType: string | null;
}

export type TransferMemberExclusionReason =
  | 'excluded_self'
  | 'missing_profile'
  | 'search_mismatch';

export interface TransferMemberDiagnostic {
  userId: string;
  included: boolean;
  reason?: TransferMemberExclusionReason;
  name?: string;
  email?: string;
}

export interface TransferMembersLoadResult {
  members: TransferMember[];
  diagnostics: {
    source: 'user_organizations' | 'user_organizations+user_profiles';
    organizationId: string;
    rawMembershipCount: number;
    afterExcludeSelf: number;
    profileRowsFound: number;
    afterProfileHydration: number;
    afterSearch: number;
    appliedFilters: string[];
    exclusions: TransferMemberDiagnostic[];
    error?: string;
  };
}

export async function loadTransferMembers(params: {
  organizationId: string;
  currentUserId: string;
  debugMode?: boolean;
}): Promise<TransferMembersLoadResult> {
  const { organizationId, currentUserId, debugMode = false } = params;
  const appliedFilters: string[] = [];

  if (!debugMode && currentUserId) {
    appliedFilters.push('Excluir usuário atual');
  }

  const empty = (error?: string): TransferMembersLoadResult => ({
    members: [],
    diagnostics: {
      source: 'user_organizations+user_profiles',
      organizationId,
      rawMembershipCount: 0,
      afterExcludeSelf: 0,
      profileRowsFound: 0,
      afterProfileHydration: 0,
      afterSearch: 0,
      appliedFilters,
      exclusions: [],
      error,
    },
  });

  const { data: memberships, error: membErr } = await supabase
    .from('user_organizations')
    .select('user_id, department_id, role_id, role_type')
    .eq('organization_id', organizationId);

  if (membErr) {
    console.error('[TaskTransfer] Erro em user_organizations:', membErr);
    return empty(membErr.message);
  }

  const rawRows = memberships ?? [];
  const rawMembershipCount = rawRows.length;
  console.info('[TaskTransfer] user_organizations retornou', rawMembershipCount, 'vínculos');

  const exclusions: TransferMemberDiagnostic[] = [];
  const eligibleRows = rawRows.filter((row) => {
    if (!debugMode && row.user_id === currentUserId) {
      exclusions.push({ userId: row.user_id, included: false, reason: 'excluded_self' });
      return false;
    }
    return true;
  });

  const afterExcludeSelf = eligibleRows.length;
  console.info('[TaskTransfer] Após excluir usuário atual:', afterExcludeSelf);

  const userIds = eligibleRows.map((r) => r.user_id);
  if (userIds.length === 0) {
    return {
      members: [],
      diagnostics: {
        source: 'user_organizations+user_profiles',
        organizationId,
        rawMembershipCount,
        afterExcludeSelf,
        profileRowsFound: 0,
        afterProfileHydration: 0,
        afterSearch: 0,
        appliedFilters,
        exclusions,
      },
    };
  }

  const { data: profiles, error: profErr } = await supabase
    .from('user_profiles')
    .select('user_id, name, email')
    .in('user_id', userIds);

  if (profErr) {
    console.error('[TaskTransfer] Erro em user_profiles:', profErr);
    return empty(profErr.message);
  }

  const profileRowsFound = profiles?.length ?? 0;
  console.info('[TaskTransfer] user_profiles retornou', profileRowsFound, 'perfis');

  const profileMap = new Map<string, { name?: string; email?: string }>();
  for (const p of profiles ?? []) {
    profileMap.set(p.user_id, { name: p.name, email: p.email });
  }

  const members: TransferMember[] = [];
  for (const row of eligibleRows) {
    const profile = profileMap.get(row.user_id);
    if (!profile) {
      exclusions.push({
        userId: row.user_id,
        included: false,
        reason: 'missing_profile',
      });
      console.warn('[TaskTransfer] Excluído — sem user_profiles:', row.user_id);
      continue;
    }

    const member: TransferMember = {
      id: row.user_id,
      name: profile.name || profile.email || row.user_id,
      email: profile.email || '',
      departmentId: row.department_id ?? null,
      roleId: row.role_id ?? null,
      roleType: row.role_type ?? null,
    };
    members.push(member);
    exclusions.push({
      userId: row.user_id,
      included: true,
      name: member.name,
      email: member.email,
    });
  }

  const afterProfileHydration = members.length;
  console.info('[TaskTransfer] Após hidratar perfis:', afterProfileHydration);
  console.table(
    exclusions.map((e) => ({
      userId: e.userId.slice(0, 8) + '…',
      incluído: e.included ? 'sim' : 'não',
      motivo: e.reason ?? '—',
      nome: e.name ?? '—',
    })),
  );

  return {
    members: members.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    diagnostics: {
      source: 'user_organizations+user_profiles',
      organizationId,
      rawMembershipCount,
      afterExcludeSelf,
      profileRowsFound,
      afterProfileHydration,
      afterSearch: afterProfileHydration,
      appliedFilters,
      exclusions,
    },
  };
}

/** Modo depuração — localStorage wf_transfer_debug=1 */
export function isTransferDebugMode(): boolean {
  try {
    return import.meta.env.DEV && localStorage.getItem('wf_transfer_debug') === '1';
  } catch {
    return false;
  }
}

export function setTransferDebugMode(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem('wf_transfer_debug', '1');
    else localStorage.removeItem('wf_transfer_debug');
  } catch {
    /* ignore */
  }
}
